import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { formValueSelector } from 'redux-form';

import type AttachmentVM from 'ab-viewModels/attachment/attachment.viewModel';
import type WorkOrderDirectoryVM from 'ab-viewModels/directory/workOrderDirectory.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { ALLOWED_FILE_TYPES_LIST } from '@acceligentllc/shared/enums/fileType';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { RootState } from 'af-reducers';

import { fromFileType } from 'ab-enums/mimeType.enum';

import { WORK_ORDER_FORM } from 'af-constants/reduxForms';

import { SegmentLabel, loadingToast, successToast } from '@acceligentllc/storybook';

import DirectoriesAttachmentTree from 'af-components/DocumentsAttachmentsTree';
import type { DirectoryEntity } from 'af-components/DocumentsAttachmentsTree/types';

import * as AttachmentActions from 'af-actions/attachment';
import * as WorkOrderActions from 'af-actions/workOrder';

export type AttachmentVMExtended = AttachmentVM & {
	directoryId?: number;
};

export interface TypedCellInfo<T> {
	original: T;
}

interface TypedColumn {
	header: string;
	id?: string;
	width?: number;
}

interface RowActionOptions {
	actionName: string;
	actionFunction: (params: unknown) => void;
	disabled?: boolean;
	show?: boolean;
}

interface RowActions {
	parentDirectory: RowActionOptions[];
	childDirectory: RowActionOptions[];
	attachmentLevel: RowActionOptions[];
}

interface OwnPropsBase {
	forwardedWorkOrderId?: number;
	isReadOnly: boolean;
	onAttachmentPreviewModalOpen?: (imageAttachmentSrc: string) => void;
}

interface ActionsEnabledProps {
	onNewFolderClick: (dir: DirectoryEntity) => void;
	tokenToRefreshDirectories: number;
	onDeleteDirectoryClick: (dir: DirectoryEntity) => void;
	onRenameAttachmentClick: (originalAttachment: AttachmentVMExtended) => void;
}

interface ActionsEnabledProps {
	onNewFolderClick: (dir: DirectoryEntity) => void;
	tokenToRefreshDirectories: number;
	onDeleteDirectoryClick: (dir: DirectoryEntity) => void;
	onRenameAttachmentClick: (originalAttachment: AttachmentVMExtended) => void;
}

type OwnProps =
	| (OwnPropsBase & { actionsEnabled: true; } & Required<ActionsEnabledProps>)
	| (OwnPropsBase & { actionsEnabled: false; } & Partial<Record<keyof ActionsEnabledProps, undefined>>);

type Props = OwnProps & ConnectedProps<typeof connector>;

const DirectoriesAndAttachments = React.memo((props: Props) => {
	const {
		getWorkOrderDirectoriesAttachments,
		downloadAttachment,
		deleteAttachment,
		formWorkOrderId,
		forwardedWorkOrderId,
		uploadAttachmentsToDirectory,
		changeAttachmentParent,
		isReadOnly,
		onAttachmentPreviewModalOpen,
		tokenToRefreshDirectories,
		onNewFolderClick,
		actionsEnabled,
		onDeleteDirectoryClick,
		onRenameAttachmentClick,
	} = props;

	const fileInputRef = React.useRef<HTMLInputElement>(null);

	const [directoriesAndAttachments, setDirectoriesAndAttachments] = React.useState<Nullable<WorkOrderDirectoryVM[]>>(null);
	const [uploadDirectoryId, setUploadDirectoryId] = React.useState<Nullable<number>>(null);

	const workOrderId: number = formWorkOrderId ?? forwardedWorkOrderId;

	const fetchData = React.useCallback(async () => {
		const fetchedData = await getWorkOrderDirectoriesAttachments(workOrderId);
		setDirectoriesAndAttachments(fetchedData);
	}, [getWorkOrderDirectoriesAttachments, workOrderId]);

	React.useEffect(() => {
		if (workOrderId || forwardedWorkOrderId) {
			fetchData();
		} else if (tokenToRefreshDirectories !== undefined) {
			fetchData();
		}
	}, [fetchData, workOrderId, forwardedWorkOrderId, tokenToRefreshDirectories]);

	const allowedFileTypes = React.useMemo(() => [ALLOWED_FILE_TYPES_LIST.map(fromFileType)].join(', '), []);

	const callDownloadAttachment = (original: AttachmentVM) => {
		const dotIndex = original.name.lastIndexOf('.');
		const attachmentNameWithoutType = (dotIndex !== -1) ? original.name.substring(0, dotIndex) : original.name;
		const fileName = `${attachmentNameWithoutType}${original.type}`;
		downloadAttachment(original.id, fileName);
	};

	const removeAttachmentFromDirectory = async (original: AttachmentVMExtended) => {
		const { id, directoryId: attachmentDirectoryId } = original;
		if (attachmentDirectoryId) {
			await deleteAttachment(id, attachmentDirectoryId);
		}

		successToast(`${id}-attachment`, { title: 'Attachment deleted', timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) });

		await fetchData();
	};

	const handleUploadPress = React.useCallback((original: WorkOrderDirectoryVM) => {
		setUploadDirectoryId(original.id);
		fileInputRef.current?.click();
	}, []);

	const uploadAttachments = React.useCallback(async (selectedDirectoryId: number, files: File[]) => {
		const toastId = `${selectedDirectoryId}-upload`;
		const startingTimestamp = TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG);

		const fileNameReference = files.length > 1
			? `${files.length} attachments`
			: files[0].name;

		loadingToast(toastId, { title: 'Uploading attachment...', description: fileNameReference, timestamp: startingTimestamp });

		await uploadAttachmentsToDirectory(selectedDirectoryId, { requestFiles: files });
		successToast(toastId, { title: 'Upload complete', description: fileNameReference, timestamp: startingTimestamp });
		await fetchData();
	}, [fetchData, uploadAttachmentsToDirectory]);

	const handleAttachmentUpload = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
		const { files } = event.target;
		if (!files || !uploadDirectoryId) {
			return;
		}
		const requestFiles: File[] = [];
		for (let i = 0; i < files.length; i += 1) {
			const file = files.item(i);
			if (file) {
				requestFiles.push(file);
			}
		}

		await uploadAttachments(uploadDirectoryId, requestFiles);
		setUploadDirectoryId(null);
	}, [uploadAttachments, uploadDirectoryId]);

	const handleAttachmentParentChange = React.useCallback(async (attachmentId: number, sourceDirectoryId: number, destinationDirectoryId: number) => {
		await changeAttachmentParent(attachmentId, sourceDirectoryId, destinationDirectoryId);
		await fetchData();
	}, [changeAttachmentParent, fetchData]);

	const rowActions: RowActions = {
		parentDirectory:
			(!actionsEnabled ?
				[]
				:
				[{
					actionName: 'New folder',
					actionFunction: onNewFolderClick,
					/**
					 * To grey out (disable) action
					 */
					disabled: false,
				}]
			),
		childDirectory: [
			...(!actionsEnabled ?
				[]
				:
				[{
					actionName: 'New folder',
					actionFunction: onNewFolderClick,
					disabled: false,
					/**
					 * Child level 3 doesn't have option to create new folder
					 */
					show: true,
				},
				]
			),
			{
				actionName: 'Upload file',
				actionFunction: handleUploadPress,
				disabled: isReadOnly,
				show: true,
			},
			...(!actionsEnabled ?
				[]
				:
				[{
					actionName: 'Delete',
					actionFunction: onDeleteDirectoryClick,
					disabled: false,
					show: !isReadOnly,
				}]
			),
		],
		attachmentLevel: [
			...(!actionsEnabled ?
				[]
				:
				[{
					actionName: 'Rename',
					actionFunction: onRenameAttachmentClick,
					show: true,
				}]
			),
			{
				actionName: 'Download',
				actionFunction: callDownloadAttachment,
				show: true,
			},
			{
				actionName: 'Delete',
				actionFunction: removeAttachmentFromDirectory,
				show: !isReadOnly,
			},
		],
	};

	const columns: TypedColumn[] = [
		{
			header: 'Category folder',
			id: 'categoryFolder',
			width: 6,
		},
		{
			header: 'Files',
			id: 'files',
			width: 1,
		},
		{
			header: 'Download',
			id: 'download',
			width: 1,
		},
		...(!isReadOnly ? [{
			header: 'Upload',
			id: 'upload',
			width: 1,
		}] : []),
		{
			header: 'Preview',
			id: 'preview',
			width: 1,
		},
		{
			header: 'Last modified',
			id: 'lastModified',
			width: 2,
		},
	];

	return (
		<>
			<div className="m-t-m m-b-m">
				<SegmentLabel
					label="Attachments"
					tooltip="Work Order related attachments. Field people have access to all of them."
				/>
			</div>
			{
				workOrderId ?
					<div className="m-b-m">
						<DirectoriesAttachmentTree
							columns={columns}
							dataTree={directoriesAndAttachments}
							fetchData={fetchData}
							handleAttachmentParentChange={handleAttachmentParentChange}
							id={workOrderId}
							isReadOnly={isReadOnly}
							onAttachmentPreviewModalOpen={onAttachmentPreviewModalOpen}
							onFileUpload={uploadAttachments}
							rowActions={rowActions}
						/>
						<input
							accept={allowedFileTypes}
							onChange={handleAttachmentUpload}
							ref={fileInputRef}
							style={{ display: 'none' }}
							type="file"
						/>
					</div>
					:
					<div className="p-l">In order to attach files, work order has to be saved first.</div>
			}
		</>
	);
});

const selector = formValueSelector(WORK_ORDER_FORM);

function mapStateToProps(state: RootState) {
	const { user: { userData, companyData } } = state;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}
	const workOrderId = selector(state, 'id');

	return {
		formWorkOrderId: workOrderId,
		companyName: companyData.name,
		selector: (fieldName: string) => selector(state, fieldName),
	};
}

function mapDispatchToProps() {
	return {
		getWorkOrderDirectoriesAttachments: WorkOrderActions.getWorkOrderDirectoriesAttachments,
		downloadAttachment: AttachmentActions.downloadAttachment,
		deleteAttachment: AttachmentActions.deleteAttachment,
		uploadAttachmentsToDirectory: AttachmentActions.uploadAttachmentsToDirectory,
		changeAttachmentParent: AttachmentActions.changeAttachmentParent,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(connector);

export default enhance(DirectoriesAndAttachments);
